import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap, timeout } from 'rxjs/operators';
import {
  applyDaisyTenantBranding,
  isDarkThemeActive,
  isTenantLightDarkOnly,
  toggleDaisyTheme,
} from '@black/shared';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../../environments/environment';

export interface TenantBranding {
  theme?: string;
  logo?: string;
  favicon?: string;
  primaryColor?: string;
  primaryContentColor?: string;
  secondaryColor?: string;
  secondaryContentColor?: string;
  accentColor?: string;
  accentContentColor?: string;
  neutralColor?: string;
  neutralContentColor?: string;
  baseColor?: string;
  baseContentColor?: string;
  customCSS?: string;
}

export interface TenantAuthSettings {
  isSystemManaged?: boolean;
  googleLoginEnabled?: boolean;
  googleClientId?: string;
  googleClientSecret?: string;
  sso?: {
    provider?: 'saml' | 'oidc';
    entryPointUrl?: string;
    issuer?: string;
    certificate?: string;
    domainRestrictions?: string[];
  };
}

export type StorageProviderType = 'GCS' | 'Bunny' | 'S3';

export interface TenantStorageBuckets {
  staticUploads?: string;
  preCourseContents?: string;
  courseContents?: string;
}

export interface TenantBunnyVideoSettings {
  enabled?: boolean;
  libraryId?: string;
  apiKey?: string;
  readOnlyApiKey?: string;
  cdnDomain?: string;
  tokenSecurityKey?: string;
  webhookSecret?: string;
  storageZoneName?: string;
  storagePassword?: string;
  storageRegion?: string;
  storagePullZoneDomain?: string;
  storageCdnSecurityKey?: string;
}

export interface TenantStorageSettings {
  provider?: StorageProviderType;
  isSystemManaged?: boolean;
  bucketName?: string;
  buckets?: TenantStorageBuckets;
  customCdnDomain?: string;
  credentials?: {
    projectId?: string;
    clientEmail?: string;
    privateKey?: string;
    [key: string]: any;
  };
  bunny?: TenantBunnyVideoSettings;
}

export interface TenantBankAccount {
  bankName: string;
  accountName: string;
  accountNumber: string;
  branchName: string;
  swiftCode?: string;
}

export interface TenantPaymentSettings {
  isSystemManaged?: boolean;
  acceptedMethods?: string[];
  payhere?: {
    merchantId?: string;
    merchantSecret?: string;
    sandbox?: boolean;
  };
  stripe?: {
    publishableKey?: string;
    secretKey?: string;
    webhookSecret?: string;
  };
  bankAccounts?: TenantBankAccount[];
}

export interface TenantLegalSettings {
  isSystemManaged?: boolean;
  companyName?: string;
  address?: string;
  city?: string;
  country?: string;
  taxId?: string;
  footerNote?: string;
  contactEmail?: string;
  contactPhone?: string;
}

export interface TenantEmailSettings {
  isSystemManaged?: boolean;
  provider?: string;
  senderEmail?: string;
  senderName?: string;
  credentials?: Record<string, any>;
  providerTemplateIds?: Record<string, string>;
}

export interface TenantSMSSettings {
  isSystemManaged?: boolean;
  provider?: string;
  senderId?: string;
  credentials?: Record<string, any>;
}

export type NotificationChannel = 'in_app' | 'email' | 'sms' | 'push' | 'whatsapp';

export interface TenantEventNotificationConfig {
  enabledChannels: NotificationChannel[];
  priority?: 'low' | 'normal' | 'high' | 'urgent';
}

export interface TenantNotificationSettings {
  isSystemManaged?: boolean;
  channels?: {
    inApp?: { enabled: boolean };
    email?: { enabled: boolean };
    sms?: { enabled: boolean; dailyLimit?: number };
    push?: { enabled: boolean };
    whatsapp?: { enabled: boolean };
  };
  events?: Record<string, TenantEventNotificationConfig>;
}

export interface TenantVideoConferencingSettings {
  isSystemManaged?: boolean;
  defaultProvider?: string;
  allowedProviders?: string[];
  provider?: string;
  zoom?: Record<string, any>;
  realtimeKit?: Record<string, any>;
  credentials?: Record<string, any>;
}

export interface TenantFeatures {
  courses?: boolean;
  liveClasses?: boolean;
  recordings?: boolean;
  certificates?: boolean;
  marketplace?: boolean;
  analytics?: boolean;
  sso?: boolean;
  autoPublishLiveClasses?: boolean;
  [key: string]: boolean | undefined;
}

export interface TenantHeroMetric {
  icon: string;
  title: string;
  subtitle: string;
}

export interface TenantHeroSettings {
  title?: string;
  subtitle?: string;
  badgeText?: string;
  primaryCtaText?: string;
  primaryCtaLink?: string;
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
  secondaryCtaIcon?: string;
  showBadge?: boolean;
  backgroundImageUrl?: string;

  // Media Slot
  mediaType?: 'portrait' | 'video' | 'batch-card' | 'custom-image';
  mediaVideoUrl?: string;
  mediaCustomImageUrl?: string;
  portraitTag?: string;
  portraitTitle?: string;
  portraitSubtitle?: string;
  portraitHighlights?: string[];

  // 3 Trust / Portfolio Metric Chips
  showMetrics?: boolean;
  metrics?: TenantHeroMetric[];
}

export interface TenantLiveClassesSectionSettings {
  enabled?: boolean;
  title?: string;
  subtitle?: string;
  maxItems?: number;
}

export interface TenantCustomContentSettings {
  enabled?: boolean;
  title?: string;
  html?: string;
}

export interface TenantFaqItem {
  question: string;
  answer: string;
}

export interface TenantInstructorSettings {
  enabled?: boolean;
  name?: string;
  title?: string;
  bio?: string;
  avatarUrl?: string;
  experienceYears?: number;
  highlights?: string[];
}

export interface TenantVideoDemoSettings {
  enabled?: boolean;
  title?: string;
  subtitle?: string;
  videoUrl?: string;
}

export interface TenantWhatsAppSettings {
  enabled?: boolean;
  phoneNumber?: string;
  message?: string;
  buttonText?: string;
  floatingButton?: boolean;
}

export interface TenantTestimonialItem {
  name: string;
  achievement?: string;
  quote: string;
  avatarUrl?: string;
}

export interface TenantTestimonialsSettings {
  enabled?: boolean;
  title?: string;
  items?: TenantTestimonialItem[];
}

export interface TenantPerkItem {
  title: string;
  description?: string;
  icon?: string;
}

export interface TenantPerksSettings {
  enabled?: boolean;
  title?: string;
  items?: TenantPerkItem[];
}

export interface TenantLandingPageSettings {
  theme?: 'modern' | 'warm' | 'bold';
  hero?: TenantHeroSettings;
  instructor?: TenantInstructorSettings;
  videoDemo?: TenantVideoDemoSettings;
  whatsApp?: TenantWhatsAppSettings;
  testimonials?: TenantTestimonialsSettings;
  perks?: TenantPerksSettings;
  customContent?: TenantCustomContentSettings;
  liveClasses?: TenantLiveClassesSectionSettings;
  showFeatures?: boolean;
  showCourses?: boolean;
  showFaq?: boolean;
  showCta?: boolean;
  faqItems?: TenantFaqItem[];
}

export interface TenantSettings {
  branding?: TenantBranding;
  features?: TenantFeatures;
  limits?: Record<string, number>;
  auth?: TenantAuthSettings;
  storage?: TenantStorageSettings;
  videoConferencing?: TenantVideoConferencingSettings;
  email?: TenantEmailSettings;
  sms?: TenantSMSSettings;
  payments?: TenantPaymentSettings;
  legal?: TenantLegalSettings;
  policies?: TenantPoliciesSettings;
  landingPage?: TenantLandingPageSettings;
  notifications?: TenantNotificationSettings;
}

export type TenantPolicyMode = 'default' | 'link' | 'custom';

export interface TenantPoliciesSettings {
  termsMode?: TenantPolicyMode;
  termsUrl?: string;
  termsContent?: string;
  termsUpdatedAt?: string | Date;
  privacyMode?: TenantPolicyMode;
  privacyUrl?: string;
  privacyContent?: string;
  privacyUpdatedAt?: string | Date;
  refundMode?: TenantPolicyMode;
  refundUrl?: string;
  refundContent?: string;
  refundUpdatedAt?: string | Date;
}

export interface TenantNavigation {
  showTutorsDirectory?: boolean;
  showTeachPage?: boolean;
  showContactPage?: boolean;
}

export interface TenantInfo {
  _id: string;
  name: string;
  slug: string;
  subdomain: string;
  ownerId?: string;
  customDomain?: string;
  status?: string;
  tier?: string;
  type?: 'SOLO_INSTRUCTOR' | 'ACADEMY' | 'MARKETPLACE';
  settings?: TenantSettings;
  billing?: Record<string, any>;
  metadata?: Record<string, any>;
  appMode?: boolean;
  navigation?: TenantNavigation;
}

const RESERVED_SUBDOMAINS = new Set([
  'www',
  'api',
  'app',
  'dev',
  'localhost',
  'staging',
  'admin',
]);

@Injectable({
  providedIn: 'root',
})
export class TenantService {
  private http = inject(HttpClient);
  private titleService = inject(Title);

  private currentTenantSubject = new BehaviorSubject<TenantInfo | null>(null);
  public currentTenant$: Observable<TenantInfo | null> =
    this.currentTenantSubject.asObservable();

  public get currentTenant(): TenantInfo | null {
    return this.currentTenantSubject.value;
  }

  private tenantSlugSubject = new BehaviorSubject<string>('default');
  public tenantSlug$: Observable<string> =
    this.tenantSlugSubject.asObservable();

  private tenantSubdomainSubject = new BehaviorSubject<string>('default');
  public tenantSubdomain$: Observable<string> =
    this.tenantSubdomainSubject.asObservable();

  private tenantNotFoundSubject = new BehaviorSubject<boolean>(false);
  public tenantNotFound$: Observable<boolean> =
    this.tenantNotFoundSubject.asObservable();

  private appModeSubject = new BehaviorSubject<boolean>(false);
  public appMode$: Observable<boolean> = this.appModeSubject.asObservable();

  private readonly CACHED_TENANT_PREFIX = 'black_cached_tenant_';

  private getStoredTenant(slug: string): TenantInfo | null {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    try {
      const raw = localStorage.getItem(this.CACHED_TENANT_PREFIX + slug);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private setStoredTenant(slug: string, tenant: TenantInfo): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      localStorage.setItem(
        this.CACHED_TENANT_PREFIX + slug,
        JSON.stringify(tenant),
      );
    } catch {
      // ignore storage quota errors
    }
  }

  private clearStoredTenant(slug: string): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      localStorage.removeItem(this.CACHED_TENANT_PREFIX + slug);
    } catch {
      // ignore storage errors
    }
  }

  /**
   * Dynamically update the PWA manifest link href to match the tenant.
   */
  public updateManifestLink(slug: string): void {
    if (typeof document === 'undefined') return;
    const manifestLink = (document.getElementById('app-manifest') ||
      document.querySelector("link[rel='manifest']")) as HTMLLinkElement;
    if (manifestLink) {
      const origin =
        typeof window !== 'undefined' && window.location.origin
          ? encodeURIComponent(window.location.origin)
          : '';
      const query = origin ? `?origin=${origin}` : '';
      manifestLink.href = `${environment.host}/api/v1/tenants/by-subdomain/${slug}/manifest.webmanifest${query}`;
    }
  }

  /**
   * Check if running as an installed PWA or standalone native app.
   * If running in standalone mode, App Mode is forced true.
   */
  public isPwaStandalone(): boolean {
    if (typeof window === 'undefined') return false;
    return Boolean(
      Capacitor.isNativePlatform() ||
        window.matchMedia?.('(display-mode: standalone)').matches ||
        (window.navigator as any)?.standalone ||
        new URLSearchParams(window.location.search).get('source') === 'pwa'
    );
  }

  /**
   * Resolves whether App Mode should be active.
   * Forced true if installed as PWA / standalone app.
   */
  public resolveAppMode(tenantAppMode?: boolean, slug?: string): boolean {
    if (this.isPwaStandalone()) {
      return true;
    }
    if (tenantAppMode !== undefined && tenantAppMode !== null) {
      return Boolean(tenantAppMode);
    }
    return (slug || this.tenantSlugSubject.value) !== 'default';
  }

  /**
   * Get the active brand name (tenant name if custom tenant, 'Learnbird' if default platform).
   */
  public getTenantBrandName(): string {
    return this.currentTenant?.name || (this.isDefaultTenant() ? 'Learnbird' : '');
  }

  private getBaseDomain(): string {
    return (
      (environment as { platformDomain?: string }).platformDomain?.toLowerCase() ||
      'learnbird.lk'
    );
  }

  /**
   * Resolve default platform home URL (e.g. learnbird.lk or localhost:4200)
   */
  public getDefaultPlatformUrl(): string {
    const baseDomain = this.getBaseDomain();
    if (typeof window === 'undefined') return `https://${baseDomain}`;

    const host = window.location.hostname.toLowerCase();
    if (
      host === 'localhost' ||
      host.endsWith('.localhost') ||
      /^\d{1,3}(\.\d{1,3}){3}$/.test(host) ||
      host.includes(':')
    ) {
      const port = window.location.port ? `:${window.location.port}` : '';
      return `${window.location.protocol}//localhost${port}/`;
    }

    return `https://${baseDomain}/`;
  }

  /**
   * Updates the document title in the browser tab to reflect the active tenant brand.
   */
  public updateDocumentTitle(pageTitle?: string): void {
    if (typeof document === 'undefined') return;
    const fullTitle = [pageTitle, this.getTenantBrandName()].filter(Boolean).join(' | ');
    if (fullTitle) {
      this.titleService.setTitle(fullTitle);
    }
  }

  private applyTenantState(tenant: TenantInfo): void {
    this.currentTenantSubject.next(tenant);
    if (tenant.slug) {
      this.tenantSlugSubject.next(tenant.slug);
    }
    if (tenant.subdomain) {
      this.tenantSubdomainSubject.next(tenant.subdomain);
    }
    this.appModeSubject.next(this.resolveAppMode(tenant.appMode, tenant.slug));
    this.applyTenantBranding(tenant.settings?.branding);
    this.updateManifestLink(tenant.subdomain || tenant.slug);
    this.updateDocumentTitle();
  }

  constructor() {
    const detectedSub = this.detectSubdomain();
    this.tenantSubdomainSubject.next(detectedSub);
    this.tenantSlugSubject.next(detectedSub);
    this.appModeSubject.next(this.resolveAppMode(undefined, detectedSub));

    const initialTenant =
      typeof window !== 'undefined'
        ? (window as any).__INITIAL_TENANT__
        : null;

    if (initialTenant?.notFound) {
      this.tenantNotFoundSubject.next(true);
    } else {
      const cached =
        initialTenant && initialTenant.name
          ? initialTenant
          : this.getStoredTenant(detectedSub);
      if (cached) this.applyTenantState(cached);
    }
  }

  /**
   * Extract subdomain from current hostname
   */
  public detectSubdomain(): string {
    if (typeof window === 'undefined') {
      return 'default';
    }

    // Support ?tenant= query parameter for local development and previewing
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const tenantQuery = urlParams.get('tenant')?.toLowerCase().trim();
      if (tenantQuery && !RESERVED_SUBDOMAINS.has(tenantQuery)) {
        return tenantQuery;
      }
    } catch {
      // ignore
    }

    const host = window.location.hostname.toLowerCase();
    const baseDomain = this.getBaseDomain();

    // If hostname is localhost, an IPv4 address, IPv6, or exact platform baseDomain, treat as default platform
    if (
      host === 'localhost' ||
      host === baseDomain ||
      /^\d{1,3}(\.\d{1,3}){3}$/.test(host) ||
      host.includes(':')
    ) {
      return 'default';
    }

    const suffix = host.endsWith('.localhost')
      ? '.localhost'
      : host.endsWith('.' + baseDomain)
        ? '.' + baseDomain
        : null;

    if (suffix) {
      const sub = host.slice(0, -suffix.length).split('.')[0];
      return RESERVED_SUBDOMAINS.has(sub) ? 'default' : sub;
    }

    // Custom domain (e.g. mathsacademy.com)
    return host;
  }

  /**
   * Get current tenant subdomain
   */
  public getTenantSubdomain(): string {
    return this.tenantSubdomainSubject.value;
  }

  /**
   * Get current tenant slug
   */
  public getTenantSlug(): string {
    return this.tenantSlugSubject.value;
  }

  /**
   * Get current tenant ID
   */
  public getTenantId(): string | null {
    return this.currentTenantSubject.value?._id || null;
  }

  /**
   * Check if the current tenant is in App Mode.
   * App Mode is forced in standalone PWA / installed app mode,
   * or enabled for all tenants except the default platform tenant.
   */
  public isAppMode(): boolean {
    return this.appModeSubject.value;
  }

  /**
   * Check if the current tenant is the default platform tenant (slug === 'default').
   */
  public isDefaultTenant(): boolean {
    if (this.tenantNotFoundSubject.value) {
      return false;
    }
    const current = this.currentTenantSubject.value;
    if (current) {
      return current.slug === 'default';
    }
    return (
      this.tenantSlugSubject.value === 'default' &&
      this.tenantSubdomainSubject.value === 'default'
    );
  }

  /**
   * Check if current tenant is a solo instructor workspace.
   */
  public isSoloInstructor(): boolean {
    return this.currentTenantSubject.value?.type === 'SOLO_INSTRUCTOR';
  }

  /**
   * Check if current tenant is an academy workspace.
   */
  public isAcademy(): boolean {
    const type = this.currentTenantSubject.value?.type;
    return type === 'ACADEMY' || !type;
  }

  /**
   * Check if Google login is enabled for the active tenant
   */
  public isGoogleLoginEnabled(): boolean {
    const tenant = this.currentTenantSubject.value;
    if (!tenant?.settings?.auth) {
      return true;
    }
    return tenant.settings.auth.googleLoginEnabled !== false;
  }

  /**
   * Get active tenant Google Client ID or fallback to environment default
   */
  public getGoogleClientId(): string {
    if (!this.isGoogleLoginEnabled()) {
      return '';
    }
    const tenant = this.currentTenantSubject.value;
    return tenant?.settings?.auth?.googleClientId || environment.googleClientId;
  }

  /**
   * Initialize tenant configuration from API
   */
  public initializeTenant(): Observable<TenantInfo | null> {
    const subdomain = this.getTenantSubdomain() || this.getTenantSlug();

    if (this.tenantNotFoundSubject.value) {
      return of(null);
    }

    return this.http
      .get<{
        success: boolean;
        data: TenantInfo;
      }>(`${environment.host}/api/v1/tenants/by-subdomain/${subdomain}`)
      .pipe(
        timeout({ first: 2500, with: () => of(null) }),
        map(
          (res: { success: boolean; data: TenantInfo } | null) =>
            res?.data || null,
        ),
        tap((tenant: TenantInfo | null) => {
          if (tenant) {
            this.tenantNotFoundSubject.next(false);
            this.setStoredTenant(subdomain, tenant);
            if (tenant.slug && tenant.slug !== subdomain) {
              this.setStoredTenant(tenant.slug, tenant);
            }
            this.applyTenantState(tenant);
          } else {
            if (subdomain !== 'default') {
              this.clearStoredTenant(subdomain);
              this.clearStoredTenant(this.tenantSlugSubject.value);
              this.tenantNotFoundSubject.next(true);
            } else {
              const cached =
                this.getStoredTenant(subdomain) ||
                this.getStoredTenant(this.tenantSlugSubject.value);
              if (cached) {
                this.applyTenantState(cached);
              } else if (
                this.tenantSlugSubject.value === 'default' &&
                this.tenantSubdomainSubject.value === 'default'
              ) {
                this.appModeSubject.next(this.resolveAppMode(false, 'default'));
              }
            }
          }
        }),
        catchError((err) => {
          console.warn('Failed to load tenant configuration:', err);
          if (subdomain !== 'default') {
            this.clearStoredTenant(subdomain);
            this.clearStoredTenant(this.tenantSlugSubject.value);
            this.tenantNotFoundSubject.next(true);
            return of(null);
          }
          const cached =
            this.getStoredTenant(subdomain) ||
            this.getStoredTenant(this.tenantSlugSubject.value);
          if (cached) {
            this.applyTenantState(cached);
            return of(cached);
          }
          if (
            this.tenantSlugSubject.value === 'default' &&
            this.tenantSubdomainSubject.value === 'default'
          ) {
            this.appModeSubject.next(this.resolveAppMode(false, 'default'));
          }
          return of(null);
        }),
      );
  }

  private isDarkThemeSubject = new BehaviorSubject<boolean>(
    isDarkThemeActive(),
  );
  public isDarkTheme$: Observable<boolean> =
    this.isDarkThemeSubject.asObservable();

  /**
   * Checks if user theme toggle is available (only when tenant uses standard light or dark theme).
   */
  public get isThemeToggleAvailable(): boolean {
    const branding = this.currentTenantSubject.value?.settings?.branding;
    return isTenantLightDarkOnly(branding);
  }

  /**
   * Whether the active document is in dark mode.
   */
  public get isDarkMode(): boolean {
    return isDarkThemeActive();
  }

  /**
   * Toggle between light and dark mode and update reactive state.
   */
  public toggleTheme(): void {
    if (!this.isThemeToggleAvailable) return;
    const branding = this.currentTenantSubject.value?.settings?.branding;
    const next = toggleDaisyTheme(branding);
    this.isDarkThemeSubject.next(next === 'dark');
  }

  /**
   * Dynamically apply CSS theme variables for DaisyUI & Tailwind
   */
  public applyTenantBranding(branding?: TenantBranding): void {
    applyDaisyTenantBranding(branding);
    this.isDarkThemeSubject.next(isDarkThemeActive());
  }
}
